import MoreHorizRoundedIcon from "@mui/icons-material/MoreHorizRounded";
import { type SxProps, Tab, Tabs, type Theme } from "@mui/material";
import { useState } from "react";
import { FONT_SIZE, INK } from "../../../../../theme/tokens";
import { MoreTabMenu } from "./MoreTabMenu";
import { SystemTabLabel } from "./SystemTabLabel";
import { MORE_TAB_ATTRIBUTE, TAB_KEY_ATTRIBUTE, tabKeyOf, useTabOverflow } from "./useTabOverflow";
import type { SystemTab, SystemTabsDensity } from "./utils";

type Props<T> = {
	tabs?: SystemTab<T>[];
	currentTab?: T;
	onTabChange?: (newTab: T) => void;
	sx?: SxProps<Theme>;
	dataTestId?: string;
	/**
	 * Lets the bar scroll sideways when its tabs are wider than it (a narrow side panel on a phone) instead of clipping
	 * the last tab. No scroll arrows — the bar is swiped. Off by default: a bar that fits stays as it was. With
	 * `moreLabel` it only scrolls once the tabs that can't move into "More" alone outgrow the bar.
	 */
	scrollable?: boolean;
	/**
	 * With `scrollable`: arrows at the ends of a bar that overflows, on pointer devices (MUI leaves them off on touch,
	 * where the bar is swiped) — for a desktop bar with more tabs than room. Off by default.
	 */
	scrollButtons?: boolean;
	/** `compact`: a smaller label and tighter padding, for a narrow side panel. Default `standard`. */
	density?: SystemTabsDensity;
	/**
	 * Already translated ("More"). Turns on the overflow menu: when the bar runs out of room its `collapsible` tabs move,
	 * last first, into a "More" tab (an ellipsis, named by this label) that opens them as a menu. The tab on screen may be
	 * one of them — a deep link to it still selects it, and the "More" tab then reads as selected. Its test id is
	 * `{dataTestId}-more`; a moved tab keeps its own `{dataTestId}-{value}` as a menu item.
	 */
	moreLabel?: string;
	/** Annie walkthrough anchor. Declared explicitly because `Props` is closed — a hyphenated attribute
	 * passed from outside would be dropped without a type error, and the tour step would never render. */
	"data-annie-target"?: string;
};

/** The "More" tab's value while the tab on screen is still in the bar — any value no caller's tab can have. */
const MORE_TAB_VALUE = "system-tabs-more";

const DENSITY_TAB_SX = {
	standard: { fontSize: "14px", padding: "4px 8px" },
	compact: { fontSize: FONT_SIZE.SECONDARY, px: 0.5, py: 0.5 },
} as const;
/** Space between a label and its badge, per density. */
const DENSITY_BADGE_GAP: Record<SystemTabsDensity, number> = { standard: 0.75, compact: 0.5 };

/**
 * SystemTabs managed tabs component. With `moreLabel`, tabs that don't fit move into a "More" menu (`useTabOverflow`).
 */
export const SystemTabs = <T,>({
	// DEFAULT: no tabs is an empty bar, as it always rendered.
	tabs = [],
	onTabChange,
	currentTab,
	sx,
	dataTestId,
	scrollable,
	scrollButtons,
	// DEFAULT: the bar every existing caller has.
	density = "standard",
	moreLabel,
	"data-annie-target": annieTarget,
}: Props<T>) => {
	const { rootRef, fit } = useTabOverflow(tabs, moreLabel !== undefined);
	const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
	const collapsed = new Set(fit.collapsed);
	const inlineTabs = tabs.filter((tab) => !collapsed.has(tabKeyOf(tab)));
	const menuTabs = tabs.filter((tab) => collapsed.has(tabKeyOf(tab)));
	const isCurrentInMenu = menuTabs.some((tab) => tab.value === currentTab);
	// With the overflow menu the bar only scrolls once moving tabs into it isn't enough.
	const scrolls = scrollable === true && (moreLabel === undefined || fit.overflows);
	const badgeGap = DENSITY_BADGE_GAP[density];

	return (
		<>
			<Tabs
				ref={rootRef}
				data-testid={dataTestId}
				data-annie-target={annieTarget}
				value={currentTab}
				variant={scrolls ? "scrollable" : "standard"}
				scrollButtons={scrolls ? (scrollButtons ? "auto" : false) : undefined}
				// Array form so a caller's `sx` (object OR function) is merged by MUI — a plain
				// `...sx` spread silently drops function-form sx (a function has no own enumerable keys).
				sx={[
					{
						minHeight: "36px",
						"& .MuiTab-root": {
							minHeight: "36px",
							textTransform: "none",
							color: INK.SECONDARY,
							...DENSITY_TAB_SX[density],
							minWidth: "auto",
							"&.Mui-selected": {
								color: INK.SECONDARY,
								fontWeight: 600,
							},
						},
						"& .MuiTabs-indicator": {
							backgroundColor: INK.SECONDARY,
						},
					},
					...(Array.isArray(sx) ? sx : [sx]),
				]}
			>
				{inlineTabs.map((tab) => (
					<Tab
						key={tabKeyOf(tab)}
						{...{ [TAB_KEY_ATTRIBUTE]: tabKeyOf(tab) }}
						// Per-tab id derived from the bar's, as `SystemSideRail` does for its items — a list of tabs
						// needs one per item for E2E targeting, and a caller that sets no `dataTestId` gets none.
						data-testid={dataTestId ? `${dataTestId}-${tabKeyOf(tab)}` : undefined}
						label={<SystemTabLabel label={tab.label} badge={tab.badge} gap={badgeGap} />}
						value={tab.value}
						disabled={tab.disabled}
						icon={tab.icon}
						iconPosition={tab.icon ? "start" : undefined}
						onClick={() => {
							if (onTabChange) {
								onTabChange(tab.value);
							}
						}}
					/>
				))}
				{moreLabel !== undefined && menuTabs.length > 0 && (
					<Tab
						{...{ [MORE_TAB_ATTRIBUTE]: "" }}
						// Takes the tab on screen's value while that tab is in the menu, so the bar marks "More" as where you are.
						value={isCurrentInMenu ? currentTab : MORE_TAB_VALUE}
						icon={<MoreHorizRoundedIcon fontSize="small" />}
						aria-label={moreLabel}
						aria-haspopup="menu"
						aria-expanded={menuAnchor !== null}
						data-testid={dataTestId ? `${dataTestId}-more` : undefined}
						onClick={(event) => setMenuAnchor(event.currentTarget)}
					/>
				)}
			</Tabs>
			{moreLabel !== undefined && (
				<MoreTabMenu
					// Closed once nothing is in the menu any more, or its tab left the page (the bar was resized while open).
					anchorEl={menuTabs.length > 0 && menuAnchor?.isConnected === true ? menuAnchor : null}
					tabs={menuTabs}
					currentTab={currentTab}
					label={moreLabel}
					onPick={(value) => {
						setMenuAnchor(null);
						onTabChange?.(value);
					}}
					onClose={() => setMenuAnchor(null)}
					dataTestId={dataTestId}
				/>
			)}
		</>
	);
};
